import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormArray, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import Decimal from 'decimal.js';
import { forkJoin, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, withLatestFrom } from 'rxjs/operators';
import { DialogTypeEnum } from 'src/app/dashboard/interfaces/DialogType';
import { DupDialogTypeEnum } from 'src/app/dashboard/interfaces/DupDialogTypeEnum';
import { ITestForDupeTransactionsRequest } from 'src/app/dashboard/interfaces/ITestForDupeTransactionsRequest';
import { NUMBER_MIN_VALUE } from 'src/app/shared/constants/constants';
import { deepCopy, getUTC, highlightRequiredControlsWithErrors } from 'src/app/shared/helpers/helpers';
import { ActivityTypesEnum } from 'src/app/shared/interfaces/ActivityTypesEnum';
import { AmendReason } from 'src/app/shared/interfaces/AmendReason';
import { BadgeColorsEnum } from 'src/app/shared/interfaces/BadgeColorsEnum';
import { IConfirmedPendingTransactionResult } from 'src/app/shared/interfaces/IConfirmedPendingTransactionResult';
import { IInventoryModel } from 'src/app/shared/interfaces/IInventoryModel';
import { ILocationToExclude } from 'src/app/shared/interfaces/ILocationToExclude.interface';
import { IValidatedTransactionDate, TransactionDateError } from 'src/app/shared/interfaces/IValidatedTransactionDate';
import { IPendingTransactionDetails, ISplitNdc } from 'src/app/shared/interfaces/pending-transaction-details.interface';
import { IPharmacy } from 'src/app/shared/interfaces/pharmacy.interface';
import { PmsIntegrationTypeEnum } from 'src/app/shared/interfaces/PmsIntegrationTypeEnum';
import { IViolatingTransactionDateNdc } from 'src/app/shared/interfaces/violating-transaction-date-ndc.interface';
import { FormattedDatePipe } from 'src/app/shared/pipes/formatted-date/formatted-date.pipe';
import { AlertMessageService } from 'src/app/shared/services/alert-message.service';
import { BackCountService } from 'src/app/shared/services/back-count.service';
import { DupTransactionsService } from 'src/app/shared/services/dup-transactions.service';
import { InventoryLocationsService } from 'src/app/shared/services/inventory-locations.service';
import { TransactionsService } from 'src/app/shared/services/transactions.service';
import { GetInventoryOnLocation, ValidateTransactionDateForInventories } from 'src/app/shared/states/inventory/inventory.actions';
import { InventoryState } from 'src/app/shared/states/inventory/inventory.state';
import { PharmacyState } from 'src/app/shared/states/pharmacy/pharmacy.state';
import { ConfirmSplitNdcsAmendment, ConfirmSplitNdcsDispense, ConfirmTheCombinedOwedAndSplitDispense, ConfirmTransaction, DeletePendingTransactionFromView, GetAmendReasonsList, InvalidateTransaction, MarkPendingTransactionAsConfirmed, RefreshPendingTransactionDetails, ResetPotentialsDuplicates, TestForDupTransactions, ValidateTransactionDateForPendingTransactionAmend } from 'src/app/shared/states/transactions/transactions.actions';
import { TransactionsState } from 'src/app/shared/states/transactions/transactions.state';
import { ValidationService } from './services/validation.service';
import { BusinessRuleViolation } from 'src/app/shared/interfaces/business-rule.interface';
import { PendingTransactionRule } from './rules/pending-transaction-rule-violation-enum';
import { c2keepPartialAddedQtyValidator } from './validators/c2keepPartialAddedQty.validator';

@Component({
  selector: 'app-pending-transaction-dialog',
  templateUrl: './pending-transaction-dialog.component.html',
  styleUrls: ['./pending-transaction-dialog.component.scss'],
  providers: [InventoryLocationsService, BackCountService],
})
@UntilDestroy()
export class PendingTransactionDialogComponent implements OnInit {
  @Input() show: boolean;
  @Input() splitNdcsOn: boolean = true;
  @Input() locationsToExclude: string[];
  @Input() hideBackCount: boolean;

  @Output() cancel: EventEmitter<void> = new EventEmitter();
  @Output() dialogConfirmationEmitted: EventEmitter<number> =
    new EventEmitter();
  @Output() openSelf: EventEmitter<void> = new EventEmitter();
  @Output() confirmed: EventEmitter<IConfirmedPendingTransactionResult> =
    new EventEmitter();
  @Output() updated: EventEmitter<IPendingTransactionDetails> =
    new EventEmitter();

  @Select(TransactionsState.amendReasonsList) amendReasonsList$: Observable<
    any[]
  >;
  @Select(TransactionsState.currentPendingTransactionDetails)
  pendingTransactionDetails$: Observable<IPendingTransactionDetails>;
  @Select(TransactionsState.loading) loading$: Observable<boolean>;
  @Select(PharmacyState.currentPharmacy)
  currentPharmacy$: Observable<IPharmacy>;
  @Select(TransactionsState.recentConfirmedTransactionOperationResult)
  confirmedTransactionOperationResult$: Observable<IConfirmedPendingTransactionResult>;
  
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;

  amendReasons: any[];

  pendingTransaction: IPendingTransactionDetails;
  showConfirmTransactionPinDialog: boolean;
  showInvalidateTransactionpinDialog: boolean;
  showDeleteConfirmationDialog: boolean;
  showFutureCycleFillAlert: boolean;
  BadgeColorsEnum = BadgeColorsEnum;
  ActivityTypesEnum = ActivityTypesEnum;
  focusPin: boolean;
  showResetPinDialog: boolean;
  showErrorPopup: boolean;
  isLackOfInventory: boolean;

  amendReason: string;

  splitNdcsForm: UntypedFormGroup;

  showAddDrugDialog: boolean;
  selectedDrugs: {ndc: string, locationID: string, drugID: number}[] = [];

  originalNdcTotalCount = 0;
  originalCount = 0;
  splitNdcsCombinedTotal = 0;
  totalCount = 0;
  hideSelf: boolean;
  showDupeDispenseModal: boolean;
  showDupAmendModal: boolean;

  transactionDate: Date;
  fillDate: string;
  isC2KeepPartialDispense: boolean;

  Math: Math = Math;
  DupDialogTypeEnum = DupDialogTypeEnum;

  backCountEnabledForTheOriginalPT: boolean;
  enabledBackCountForSplitNdcIDs: string[] = [];

  scriptModified: boolean;

  c2keepPartialAddedQtyControlErrorsDescription: any = {};

  @ViewChild('cancelButton') cancelButton;
  @ViewChild('confirmButton') confirmButton;
  @ViewChild('amendReasonDropdown') amendReasonDropdown;
  @ViewChild('amendReasonOtherDescription') amendReasonOtherDescription;
  @ViewChild('transactionDatePicker') transactionDatePicker;

  constructor(
    private store: Store,
    private transactionsService: TransactionsService,
    private formBuilder: UntypedFormBuilder,
    private formattedDatePipe: FormattedDatePipe,
    private dupTransactionsService: DupTransactionsService,
    private cd: ChangeDetectorRef,
    private inventoryLocationsService: InventoryLocationsService,
    private backCountService: BackCountService,
    private alertService: AlertMessageService,
    private validator: ValidationService
  ) {
    this.splitNdcsForm = this.initForm();
  }

  ngOnInit(): void {
    if (!this.store.selectSnapshot(TransactionsState?.amendReasonsList)?.length) {
      this.store.dispatch(new GetAmendReasonsList())
    }

    this.amendReasonsList$
    .pipe(untilDestroyed(this))
    .subscribe(list => {
      this.amendReasons = list?.filter(x => x?.name !== 'Returned to wholesaler');
    })

    this.pendingTransactionDetails$
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res) {
          this.pendingTransaction = { ...deepCopy(res), note: null };
          
          this.setUpDates();

          if (this.pendingTransaction.activityType === ActivityTypesEnum.Amendment) {
            this.splitNdcsForm.addControl('amendReason',new UntypedFormControl('', Validators.required));
          }

          this.setUpBackCount();
          this.updateLackOfInventoryModalView(this.pendingTransaction?.count);
          this.originalNdcTotalCount = Math.abs(res?.count);
          this.originalCount = Math.abs(this.originalNdcTotalCount);
          this.pendingTransaction.availableSOH = this.pendingTransaction.similarOnHand - this.Math.abs(this.originalCount);
          this.setC2KeepPartialControl();
          this.adjustSOH(this.pendingTransaction.gpi);
          this.setSOHForSplitNdcAmends();
        }
      });

    setTimeout(() => {
      this.cd.detectChanges();
    });

    this.updateOriginalTransactionOnHandWhenLocationChanges();

    this.transactionsService.pendingTransactionModified$
    .pipe(untilDestroyed(this))
    .subscribe(id => {
      if (id && id === this.pendingTransaction.id) {
        this.scriptModified = true;
      }
    });

    this.splitNdcsForm
      .get('c2keepPartialAddedQty')
      .valueChanges.pipe(untilDestroyed(this), distinctUntilChanged(), debounceTime(250))
      .subscribe((val) => {
        this.setTotalCount();
        this.adjustSOH(this.pendingTransaction.gpi);
      });
  }

  validateBeforeConfirming() {
    if (this.isConfirmButtonDisabled()) {
      highlightRequiredControlsWithErrors(this.splitNdcsForm);
     return;
    }

    const violations = this.validator.validateBeforeConfirming(this.pendingTransaction);
    if (violations.length) {
      this.handleRuleViolations(violations);
      return;
    }
    this.confirmTransaction();
  }


  confirmTransaction() {
    if (this.pendingTransaction.activityType === ActivityTypesEnum.Amendment) {
      this.confirmAmend();
    } else {
      this.confirmDispense();
    }
  }

  confirmDispense() {
    const payload = this.getPayloadToValidateTransactionDateForDispense();

    let requests = [this.store.dispatch(new ValidateTransactionDateForInventories(payload))];

    if (!this.dupTransactionsService.isDup(this.pendingTransaction.drugId, ) && !this.isSplitNdcsAnyDupes()) { 
      const testForDupesPayload: ITestForDupeTransactionsRequest = {
        rxNumber: this.pendingTransaction.rxNumber,
        transactionDate: payload.transactionDate,
        fillNumber: this.pendingTransaction.refillNumber,
        ndcs: payload.inventoryActions?.map(x => ({
          drugId: x.drugId, 
          addedQuantity: 0 - Math.abs(x.addedQuantity)
        })),
        activityType: ActivityTypesEnum.Dispensed
      };
      requests.push(this.store.dispatch(new TestForDupTransactions(testForDupesPayload)));
    }
   

    forkJoin(requests)
      .pipe(untilDestroyed(this))
      .subscribe((results) => {
        const [response1, response2] = results;
        const validateTrDateResponse = response1.inventory?.response;
        const duplicateDispenses = response2?.transactions?.duplicateTransactions || [];
        if (
          validateTrDateResponse?.success &&
          validateTrDateResponse?.value?.length
        ) {
          this.markConflictingNdcs(validateTrDateResponse.value?.map((x) => ({ drugId: x.drugId, locationId: x.locationId})));
          this.transactionsService.handleTransactionDateErrors(
            validateTrDateResponse.value
          );
          this.isC2KeepPartialDispense = false;
        } else if (duplicateDispenses?.length > 0) {
          this.hideSelf = true;
          this.showDupeDispenseModal = true;
        } else if (
          validateTrDateResponse?.success &&
          !validateTrDateResponse?.value?.length &&
          !duplicateDispenses?.length
        ) {
          this.showConfirmTransactionPinDialog = true;
        }

        this.cd.detectChanges();
      });
  }

  confirmAmend() {
    const validateTrDateAmend = this.getPayloadToValidateTransactionDateForAmend();

    let requests = [this.store.dispatch(new ValidateTransactionDateForPendingTransactionAmend(validateTrDateAmend))];

    if (!this.dupTransactionsService.isDup(this.pendingTransaction.drugId) && !this.isSplitNdcsAnyDupes()) {
      const testForDupeAmend: ITestForDupeTransactionsRequest = {
        rxNumber: this.pendingTransaction.rxNumber,
        transactionDate: validateTrDateAmend.transactionDate,
        fillNumber: this.pendingTransaction.refillNumber,
        activityType: ActivityTypesEnum.Amendment,
        ndcs: this.pendingTransaction.splitNdcs.length ? 
              this.pendingTransaction?.splitNdcs?.map(x => ({drugId: x.drugId, addedQuantity: Math.abs(x.count)})) : 
              [{drugId: this.pendingTransaction.drugId, addedQuantity: this.pendingTransaction.count}],
      };

      requests.push(this.store.dispatch(new TestForDupTransactions(testForDupeAmend)));
    }

    forkJoin(requests)
    .pipe(untilDestroyed(this))
      .subscribe((results) => {
        const [response1, response2] = results;
        const validateTrDateResponse = response1?.transactions?.response?.value as IValidatedTransactionDate[];
        let duplicateDispenses = [];

        if (response2) { //if test for dup amends was sent
          duplicateDispenses = this.store.selectSnapshot(TransactionsState.duplicateTransactions) || [];
        }
        
        if (validateTrDateResponse.every((x) =>
              x.transactionDateError === TransactionDateError.none
          ) && duplicateDispenses?.length === 0
        ) {
          this.showConfirmTransactionPinDialog = true;
        } else if (duplicateDispenses?.length === 0) {
          this.splitNdcsForm.get('transactionDateDisplayValue')?.setErrors({ causedNegativeBalance: true });
          this.transactionsService.handleTransactionDateErrors(validateTrDateResponse as any);
        } else {
          this.hideSelf = true;
          this.showDupAmendModal = true;
        }
        this.cd.detectChanges();
      });
  }

  markConflictingNdcs(conflictingNdcs: IViolatingTransactionDateNdc[]): void {
    const ndcs = this.splitNdcsForm?.controls?.ndcs as UntypedFormArray;

    conflictingNdcs?.forEach((conflictingNdc) => {
      const drugControl = ndcs.controls.find(
        (control) => control.value.drugId === conflictingNdc.drugId && control.value.locationId === conflictingNdc.locationId
      );
      drugControl?.get('addedQty')?.setErrors({ causedNegativeBalance: true });
      const transactionDateDisplayValueControl = this.splitNdcsForm.get('transactionDateDisplayValue');
      transactionDateDisplayValueControl.markAsDirty();
      transactionDateDisplayValueControl.setErrors({ causedNegativeBalance: true });
      this.transactionDatePicker.validate(transactionDateDisplayValueControl);
    });
  }

  changeReason(e) {
    if (e.id === AmendReason.Other) {
      this.splitNdcsForm
        .get('amendReasonOtherDescription')
        .setValidators([Validators.required, Validators.minLength(1)]);
      this.amendReasonOtherDescription?.focusInput();
    } else {
      const amendReasonOtherDescription = this.splitNdcsForm.get(
        'amendReasonOtherDescription'
      );
      amendReasonOtherDescription.setValue(null);
      amendReasonOtherDescription.clearValidators();
      amendReasonOtherDescription.updateValueAndValidity();
    }
    this.amendReason = e.name;
  }

  openResetPinDialog(): void {
    this.focusPin = false;
    this.showConfirmTransactionPinDialog = false;
    this.showResetPinDialog = true;
  }

  afterPinHandled({ success, pin }): void {
    if (success) {
      if (this.showConfirmTransactionPinDialog) {
        this.continueWithConfirmApi(pin);
      } else if (this.showInvalidateTransactionpinDialog) {
        this.continueWithInvalidateApi(pin);
      }
      this.store.dispatch(new ResetPotentialsDuplicates());
    }
  }

  continueWithConfirmApi(pin: number) {
    if (
      this.splitNdcsForm?.controls?.ndcs?.value?.length ||
      this.pendingTransaction?.isSplitNdc
    ) {
      this.confirmSplitNdcsTransaction(pin);
    } else {
      this.confirmSingleTransaction(pin);
    }
  }

  private confirmSplitNdcsTransaction(pin: number) {
    const addedDrugs = this.splitNdcsForm?.controls?.ndcs?.value;
    const c2keepPartialAddedQty = this.splitNdcsForm.get('c2keepPartialAddedQty').value;
    const isLackOfInventory = (this.pendingTransaction.starting - this.originalCount) < 0 && this.pendingTransaction.activityType === ActivityTypesEnum.Dispensed;
    let payload: any = {
      id: this.pendingTransaction.id,
      pin,
      amendReason: this.amendReason,
      amendReasonOtherDescription: this.splitNdcsForm.get(
        'amendReasonOtherDescription'
      ).value,
      originalNdcQty: isLackOfInventory && !!c2keepPartialAddedQty ? c2keepPartialAddedQty : this.originalCount,
      note: this.pendingTransaction?.note,
      transactionDate: this.transactionDate,
      originalTransactionLocationId: this.splitNdcsForm.get('originalTransactionLocationId').value,
      isBackCountMatches: this.splitNdcsForm.get('originalTransactionIsBackCountMatches')?.value,
      splitNdcs:
        this.pendingTransaction?.activityType === ActivityTypesEnum.Dispensed
          ? addedDrugs.map((x) => ({
              drugId: x?.drugId,
              addedQty: +x?.addedQty,
              note: x?.note,
              locationId: x?.locationId,
              isBackCountMatches: x?.isBackCountMatches
            }))
          : this.pendingTransaction?.splitNdcs?.map((x, index) => (
            {
              id: x.id, 
              isBackCountMatches: (this.splitNdcsForm?.get('amendSplitNdcs') as FormArray)?.at(index)?.get('isBackCountMatches')?.value
            }
          )),
    };
    let request: any = {};
    if (isLackOfInventory) {
      payload.locationId = this.splitNdcsForm.get('originalTransactionLocationId')?.value;
      payload.c2KeepPartialDispenseAddedQty = this.splitNdcsForm.get('c2keepPartialAddedQty')?.value;
      request = new ConfirmTheCombinedOwedAndSplitDispense(payload)
    } else if (this.pendingTransaction?.activityType === ActivityTypesEnum.Dispensed) {
      request = new ConfirmSplitNdcsDispense(payload);
    } else {
      request = new ConfirmSplitNdcsAmendment(payload);
    }
    this.store
      .dispatch(
        request
      )
      .pipe(take(1))
      .subscribe(() => {
        this.amendReason = '';
        this.closePinDialog();
        this.cancel.emit();
        this.backCountService.handleBackCountPillUpdate([{isBackCountMatches: payload.isBackCountMatches}, payload.splitNdcs]);
        this.backCountService.handleBackCountPillUpdate(this.splitNdcsForm?.get('amendSplitNdcs')?.getRawValue());
        this.transactionsService.refreshTransactionsTable$.next();
      });
  }

  

  private confirmSingleTransaction(pin: number) {
    let payload = {
      id: this.pendingTransaction.id,
      note: this.pendingTransaction.note,
      locationId: this.splitNdcsForm.get('originalTransactionLocationId')?.value,
      pin,
      amendReason: this.amendReason,
      amendReasonOtherDescription: this.splitNdcsForm.get(
        'amendReasonOtherDescription'
      ).value,
      transactionDate: this.transactionDate,
      isC2KeepPartialDispense: this.isC2KeepPartialDispense,
      c2KeepPartialDispenseAddedQty: this.splitNdcsForm.get('c2keepPartialAddedQty')?.value,
      isBackCountMatches: this.splitNdcsForm.get('originalTransactionIsBackCountMatches')?.value
    };
    this.store
      .dispatch(new ConfirmTransaction(payload))
      .pipe(take(1), withLatestFrom(this.confirmedTransactionOperationResult$))
      .subscribe(([_, result]) => {
        this.amendReason = '';
        this.closePinDialog();
        this.cancel.emit();
        if (this.confirmButton) this.confirmButton.disabled = false;
        const unconfirmedId =
          this.store.selectSnapshot(TransactionsState).unconfirmedDispenseId;
        if (!unconfirmedId) {
          this.transactionsService.refreshTransactionsTable$.next();
        } else {
          this.showErrorPopup = true;
        }
        this.backCountService.handleBackCountPillUpdate(payload);
        this.confirmed.next(result);
      });
  }

  continueWithInvalidateApi(pin: number) {
    let payload = {
      id: this.pendingTransaction.id,
      pin,
    };
    this.closePinDialog();
    this.store
      .dispatch(new InvalidateTransaction(payload))
      .pipe(take(1))
      .subscribe(() => {
        this.cancel.emit();
      });
  }

  closePinDialog(): void {
    this.showConfirmTransactionPinDialog = false;
    this.showInvalidateTransactionpinDialog = false;
    this.focusPin = false;
    this.selectedDrugs = [];
    this.isC2KeepPartialDispense = false;
    this.hideSelf = false;
  }

  openConfirmationDialog() {
    const unconfirmedId =
      this.store.selectSnapshot(TransactionsState).unconfirmedDispenseId;
    if (unconfirmedId) this.dialogConfirmationEmitted.next(unconfirmedId);

    this.showErrorPopup = false;
  }

  openReceivingModal() {
    this.cancel.emit();
    this.transactionsService.openReceivingDialogRequested$.next();
  }

  partialDispense() {
    if (this.splitNdcsForm.invalid) {
      highlightRequiredControlsWithErrors(this.splitNdcsForm);
      return;
    } 
    
    this.isC2KeepPartialDispense = true;
    const violations = this.validator.validateBeforeConfirming(this.pendingTransaction);
    if (violations.length) {
      this.handleRuleViolations(violations);
      return;
    }
     else {
       this.confirmDispense();
     }
  }

  addSelectedDrug(drug: IInventoryModel) {
    this.inventoryLocationsService.addLocationsWithPositiveInventory(drug.drugID, drug.positiveInventoryBalanceLocationIDs);
    const backCountEnabledForTheActivity = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.Dispensed, drug.schedule, drug.drugID);
    const ndcs = this.splitNdcsForm.controls.ndcs as UntypedFormArray;
    const newNdc = this.formBuilder.group({
      packageNdc: drug?.packageNdc11Digits,
      friendlyName: drug?.friendlyName,
      addedQty: [0, [Validators.required, Validators.min(0)]],
      onHand: drug?.onHand,
      note: '',
      drugId: drug?.drugID,
      locationId: drug.locationID ?? this.store.selectSnapshot(InventoryState.primaryLocationID),
      isBackCountMatches: [null, backCountEnabledForTheActivity ? Validators.required : null],
      gpi: drug.gpi,
      actualSOH: drug.similarOnHand || 0,
      availableSOH: null
    });
    
    newNdc.get('locationId').valueChanges
    .pipe(untilDestroyed(this), distinctUntilChanged())
    .subscribe(id => {
      this.locationChanged(newNdc, id);
      this.resetBackCount(newNdc.get('isBackCountMatches'));
    })

    newNdc.valueChanges
      .pipe(untilDestroyed(this), distinctUntilChanged((prev, curr) => prev?.addedQty === curr?.addedQty), debounceTime(250))
      .subscribe((data) => {
        this.onValueChanged(data);
        setTimeout(() => {
          this.adjustSOH(drug.gpi);
        });
      });

    ndcs.push(newNdc);
    
    this.setTotalCount();
    this.adjustSOH(drug.gpi);
    this.updateLackOfInventoryModalView();

    setTimeout(() => {
      (<HTMLElement>document
        .querySelector('tbody')
        .querySelector(`tr[ng-reflect-name="${ndcs.length - 1}"]`)
        ?.querySelector('input'))?.focus();
    });
  }

  private adjustSOH(gpi: string): void {
    if (!gpi) {
      return;
    }
    const sign = this.pendingTransaction.activityType === ActivityTypesEnum.Dispensed ? -1 : 1;
    
    if (this.isLackOfInventory && this.pendingTransaction.similarOnHand) {
      this.pendingTransaction.availableSOH = new Decimal(this.pendingTransaction.similarOnHand).sub(this.Math.abs(this.getC2keepPartialAddedQty())).toNumber();
    } else if  (this.pendingTransaction.similarOnHand) {
      this.pendingTransaction.availableSOH = new Decimal(this.pendingTransaction.similarOnHand).add(new Decimal(this.Math.abs(this.originalCount)).mul(sign)).toNumber();
    }
    const rows = (this.splitNdcsForm.get('ndcs') as UntypedFormArray)
    .controls.filter(control => control.value.gpi === gpi);
      if (rows?.length) {
          const originalTransactionHasTheSameGpi = this.pendingTransaction.gpi === gpi;
          const actualSOH = originalTransactionHasTheSameGpi ? new Decimal(this.pendingTransaction.availableSOH) : new Decimal(rows[0].value.actualSOH || 0);
          let newSOH = rows
          .map(x => +x.value.addedQty)
          .reduce((acc, count) => acc.sub(new Decimal(count)), actualSOH)
          .toNumber();
    
          rows.forEach(control => {
            const value = control.value;
            if (!!value.gpi) {
              control.get('availableSOH').setValue(newSOH);
            }
          });
          if (originalTransactionHasTheSameGpi) {
            this.pendingTransaction.availableSOH = newSOH;
          }
      }
  }

  private setSOHForSplitNdcAmends(): void {
    if (!this.pendingTransaction.isSplitNdc && this.pendingTransaction.activityType !== ActivityTypesEnum.Amendment) {
      return;
    }
    const uniqueGpis = [...new Set(this.pendingTransaction.splitNdcs.filter(x => !!x.gpi).map(x => x.gpi))];
    
    uniqueGpis.forEach(gpi => {
      const ndcsWithThisGpi = this.pendingTransaction.splitNdcs.filter(x => x.gpi === gpi);
      if (ndcsWithThisGpi?.length) {
        const actualSOH = new Decimal(ndcsWithThisGpi[0].similarOnHand);
        let newSOH = ndcsWithThisGpi
        .map(x => +x.count)
        .reduce((acc, count) => acc.add(new Decimal(count)), actualSOH)
        .toNumber();
        ndcsWithThisGpi.forEach(x => x.availableSOH = newSOH);
      }
    });

  }

  private locationChanged(ndc: FormGroup, locationID: string): void {
    this.store.dispatch(new GetInventoryOnLocation(locationID, ndc.get('drugId').value))
    .pipe(untilDestroyed(this))
    .subscribe(() => {
      const currentInventoryOnLocation = this.store.selectSnapshot(InventoryState.currentInventoryForNdcOnLocation);
      ndc.get('onHand').setValue(currentInventoryOnLocation.onHand);
      ndc.updateValueAndValidity();
    });
  }

  private setTotalCount(): void {
    if (this.isLackOfInventory) {
      this.splitNdcsCombinedTotal = this.recalcSplitNdcsCombinedTotal();
      const c2KeepPartialDispenseAddedQty = this.getC2keepPartialAddedQty();
      this.totalCount = new Decimal(this.splitNdcsCombinedTotal).add(new Decimal(c2KeepPartialDispenseAddedQty)).toNumber();
    } else {
      this.totalCount =  new Decimal(this.splitNdcsCombinedTotal).add(this.originalCount).toNumber();
    }
  }

  onValueChanged(data) {
    const ndcs = this.splitNdcsForm.controls.ndcs as UntypedFormArray;
    const combinedTotal = this.recalcSplitNdcsCombinedTotal();
    this.splitNdcsCombinedTotal = combinedTotal;

    this.originalCount = this.getOriginalNdcCountInSplitNdcWorkflow(combinedTotal);

    const packageNdc = data?.packageNdc.trim();
    const addedQtyControl = ndcs.controls
      .find((x) => x?.value?.packageNdc?.trim() == packageNdc)
      ?.get('addedQty');
    this.updateLackOfInventoryModalView();

    //suppressLeadingZero
    if (
      addedQtyControl?.value.length > 1 &&
      addedQtyControl?.value.startsWith('0')
    ) {
      addedQtyControl.setValue(addedQtyControl.value.substring(1));
    }

    if (this.originalCount < 0 && addedQtyControl) {
      addedQtyControl.setErrors({ addedAmountExceeded: true });
    }

    if (this.originalCount >= 0) {
      ndcs.controls.map((control) => {
        control?.get('addedQty').setErrors({ addedAmountExceeded: null });
        control?.get('addedQty').updateValueAndValidity({ emitEvent: false });
      });
      if (this.confirmButton) this.confirmButton.disabled = false;
    }

    this.setTotalCount();

    this.splitNdcsForm.updateValueAndValidity();
  }

  trackByIndex(index, item) {
    return item?.drugId + item?.locationId;
  }

  deleteRow(i: any) {
    const ndcs = this.splitNdcsForm.controls.ndcs as UntypedFormArray;
    const dateTime = this.splitNdcsForm.controls.transactionDateDisplayValue;
    const amendReasonOtherDescription = this.splitNdcsForm.controls.amendReasonOtherDescription;
    const originalTrLocationID = this.splitNdcsForm.controls.originalTransactionLocationId;
    const originalTrIsBackCountMatches = this.splitNdcsForm.controls.originalTransactionIsBackCountMatches;
    const c2keepPartialAddedQty = this.splitNdcsForm.controls.c2keepPartialAddedQty;
    const drugID = ndcs.getRawValue()[i].drugId;
    const gpi = ndcs.getRawValue()[i].gpi;
    //The same drug can be added from a few locations. Don't remove back count then.
    const shouldRemoveBackCountForTheDrug = (ndcs.value as any[])?.filter(x => x.drugId === drugID)?.length <= 1;

    setTimeout(() => {
      ndcs.removeAt(i);

      if (shouldRemoveBackCountForTheDrug) {
        this.backCountService.removeDrugIDRequiringBackCount(drugID);
      }
      this.splitNdcsForm = this.formBuilder.group({
        ndcs: ndcs,
        transactionDateDisplayValue: dateTime,
        amendReasonOtherDescription,
        originalTransactionLocationId: originalTrLocationID,
        originalTransactionIsBackCountMatches: originalTrIsBackCountMatches,
        c2keepPartialAddedQty
      });
     
      this.splitNdcsForm.updateValueAndValidity();
      this.originalCount = this.getOriginalNdcCountInSplitNdcWorkflow();
      this.splitNdcsCombinedTotal = this.recalcSplitNdcsCombinedTotal();
      this.updateLackOfInventoryModalView();
      this.setTotalCount();
      this.adjustSOH(gpi);
    });
  }

  deleteConfirmed() {
    this.showDeleteConfirmationDialog = false;
    this.showInvalidateTransactionpinDialog = true;
  }

  invalidatePendingTransaction() {
    this.showDeleteConfirmationDialog = true;
  }

  getLatest() {
    this.store
      .dispatch(
        new RefreshPendingTransactionDetails(this.pendingTransaction.id)
      )
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        const isCardRemoved =
          this.store.selectSnapshot(
            TransactionsState
          ).currentPendingTransactionRemoved;
        if (!isCardRemoved) {
          this.updated.emit(this.pendingTransaction);
        } else {
          this.cancel.emit();
        }
      });
  }

  onAddNewDrugClick() {
    this.selectedDrugs = this.getSelectedDrugsOnLocations();

    this.showAddDrugDialog = true;
  }
  
  private getSelectedDrugsOnLocations(): {drugID: number, ndc: string, locationID: string}[] {
    let originalNdc = [
      {
        drugID: this.pendingTransaction.drugId,
        ndc: this.pendingTransaction.packageNdc, 
        locationID: this.splitNdcsForm.get('originalTransactionLocationId')?.value,
      }
    ];

    if (this.splitNdcsForm?.controls?.ndcs?.value?.length) {
      return [
       ...originalNdc,
        ...this.splitNdcsForm?.controls?.ndcs?.value?.map((x) => ({
          ndc: x.packageNdc.trim(), 
          locationID: x.locationId,
          drugID: x.drugId
        })
        ),
      ];
    }
    return originalNdc;
  }

  cancelHandler() {
    this.splitNdcsForm = this.initForm();
    this.store.dispatch(new ResetPotentialsDuplicates());
    this.cancel.emit();
  }

  getLogoUrl() {
    switch (this.pendingTransaction?.integrationType) {
      case PmsIntegrationTypeEnum.LibertyRx:
        return '/assets/images/liberty-logo.png';
      case PmsIntegrationTypeEnum.PioneerRx:
        return '/assets/images/pioneerRx-logo.png';
      case PmsIntegrationTypeEnum.FrameworkLTC:
        return '/assets/images/frameworkLTC-logo.png';

      default:
        break;
    }
  }

  onBackFromNewDrugModal() {
    this.showAddDrugDialog = false;
    this.openSelf.emit();
    this.updateLackOfInventoryModalView();
  }

  addNoteToOriginalPendingTransaction(note: string) {
    this.pendingTransaction.note = note;
  }

  removeNoteFromOriginalPendingTransaction() {
    this.pendingTransaction.note = null;
  }

  saveAddedNote(note: string, i: number): void {
    this.getNoteControlOfDrugAt(i)?.setValue(note);
  }

  removeNote(i: number): void {
    this.getNoteControlOfDrugAt(i)?.setValue(null);
  }

  private updateLackOfInventoryModalView(addedQty: number = this.originalCount) {
    if (this.pendingTransaction.activityType !== ActivityTypesEnum.Dispensed) {
      return;
    }

    this.isLackOfInventory =
      new Decimal(+this.pendingTransaction?.starting)
        .sub(new Decimal(this.Math.abs(+addedQty)))
        .toNumber() < 0;
  }

  private initForm(): UntypedFormGroup {
    return this.formBuilder.group({
      ndcs: this.formBuilder.array([]),
      amendSplitNdcs: this.formBuilder.array([]),
      transactionDateDisplayValue: '',
      amendReasonOtherDescription: '',
      originalTransactionLocationId: this.store.selectSnapshot(InventoryState.primaryLocationID),
      originalTransactionIsBackCountMatches: null,
      c2keepPartialAddedQty: ''
    });
  }

  amountExceededError(): boolean {
    return (this.splitNdcsForm?.controls?.ndcs as UntypedFormArray).controls?.some(
      (control) => {
        const addedQtyControl = control.get('addedQty');
        return !!addedQtyControl.errors?.addedAmountExceeded;
      }
    );
  }

  getNoteControlOfDrugAt(index: number): AbstractControl {
    return (<UntypedFormArray>this.splitNdcsForm.controls.ndcs)
      ?.at(index)
      ?.get('note');
  }

  dateTimeChanged(date): void {
    this.transactionDate = date.value > new Date() ? new Date() : date.value;
    this.removeNegativeBalanceErrorFromAllAddedQtyControls();
  }

  private removeNegativeBalanceErrorFromAllAddedQtyControls() {
    const ndcs = this.splitNdcsForm?.controls?.ndcs as UntypedFormArray;
    ndcs.controls.forEach((ndc) => {
      ndc.get('addedQty').setErrors({ causedNegativeBalance: null });
      ndc.get('addedQty').updateValueAndValidity({ emitEvent: false });
    });
  }

  now(): Date {
    return new Date();
  }

  private getPayloadToValidateTransactionDateForDispense() {
    const originalAddedQty = this.getOriginalAddedQty();
    const addedNdcs = (this.splitNdcsForm?.controls?.ndcs?.value as any[])
      ?.filter((x) => +x?.addedQty > 0)
      ?.map((x) => ({
        drugId: x.drugId,
        addedQuantity: x.addedQty,
        activityType: DialogTypeEnum.Dispense,
        packageNdc: x.packageNdc,
        locationId: x.locationId
      }));

    let originalTransaction = {
      drugId: this.pendingTransaction.drugId,
      addedQuantity: originalAddedQty,
      packageNdc: this.pendingTransaction.packageNdc,
      locationId: this.splitNdcsForm.get('originalTransactionLocationId')?.value ?? this.store.selectSnapshot(InventoryState.primaryLocationID)
    };
    if (originalTransaction.addedQuantity > 0) {
      return {
        transactionDate: this.transactionDate,
        inventoryActions: [originalTransaction, ...addedNdcs],
      };
    }

    return {
      transactionDate: this.transactionDate,
      inventoryActions: addedNdcs,
    };
  }

  private getPayloadToValidateTransactionDateForAmend() {
    const originalAddedQty = this.getOriginalAddedQty();

    return {
      amendId: this.pendingTransaction.id,
      transactionDate: this.transactionDate,
      addedQuantity: originalAddedQty,
      amendmentReasonString: this.amendReason,
    };
  }

  private getOriginalAddedQty(): number {
    if (!this.isLackOfInventory) {
      const originalAddedQty =
        Math.abs(this.pendingTransaction.count) -
        this.splitNdcsForm?.controls?.ndcs?.value?.reduce(
          (accumulator, currentObject) => {
            const addedQty = new Decimal(Math.abs(currentObject.addedQty));
            return new Decimal(accumulator).add(addedQty).toNumber();
          },
          0
        );
      return originalAddedQty;
    }
    return  this.splitNdcsForm.get('c2keepPartialAddedQty').value;
  }

  recordDupes(): void {
    this.showDupeDispenseModal = false;
    this.showDupAmendModal = false;
    this.showConfirmTransactionPinDialog = true;
  }

  reviewed(): void {
    this.showDupeDispenseModal = false;
    this.showDupAmendModal = false;
    this.hideSelf = false;
  }

  markPendingTransactionAsConfirmed(): void {
    const dupes =
      this.store.selectSnapshot(TransactionsState.duplicateTransactions);
    const dupe = dupes?.find(
      (x) => x.drugId === this.pendingTransaction.drugId
    );
    if (dupe) {
      const payload = {
        pendingTransactionId: this.pendingTransaction.id,
        confirmedTransactionId: dupe.id,
        confirmedTransactionTransactionDate: dupe.transactionDate,
      };
      this.store
        .dispatch(new MarkPendingTransactionAsConfirmed(payload))
        .pipe(take(1))
        .subscribe((_) => {
          const response =
            this.store.selectSnapshot(TransactionsState)?.response;
          if (response?.success) {
            this.store.dispatch(
              new DeletePendingTransactionFromView(this.pendingTransaction.id)
            );
          }
          this.cancel.emit();
        });
    } else {
      //original transaction is not a duplicate
      this.cancel.emit();
    }
  }

  removeSplitNdcAmend(drugId: number): void {
    this.pendingTransaction.splitNdcs = this.pendingTransaction.splitNdcs.filter(x => x.drugId !== drugId);
    if (!this.pendingTransaction.splitNdcs?.length) {
      this.markPendingTransactionAsConfirmed();
    }
  }

  isConfirmButtonDisabled(): boolean {
    this.splitNdcsForm.get('c2keepPartialAddedQty').updateValueAndValidity();
    return (
      (this.pendingTransaction?.activityType === ActivityTypesEnum.Amendment &&
        (!this.amendReason || this.amendReason?.length < 5)) ||
      this.splitNdcsForm.invalid
    );
  }

  dropDownTab() {
    this.transactionDatePicker?.focus();
  }

  datePickerTabEnter() {
    if (this.amendReason === 'Other') {
      this.amendReasonOtherDescription?.focusInput();
    } else {
      this.focusControlButtons();
    }
  }

  otherAmendReasonDescriptionTabEnter() {
    this.focusControlButtons();
  }

  private focusControlButtons() {
    if (this.isConfirmButtonDisabled()) {
      setTimeout(() => {
        this.cancelButton?.focus();
      });
    } else {
      setTimeout(() => {
        this.confirmButton?.focus();
      });
    }
  }

  controlButtonTab() {
    if (this.pendingTransaction.activityType !== ActivityTypesEnum.Amendment) {
      this.transactionDatePicker.focus();
    }
  }

  controlButtonFocusNext(): void {
    event.preventDefault();
    if (this.pendingTransaction.activityType !== ActivityTypesEnum.Amendment) {
      this.transactionDatePicker.focusInput();
    } else this.amendReasonDropdown.focus();
  }

  shouldDisplayGetLatestButton(): boolean {
    return (
      this.pendingTransaction.activityType !== ActivityTypesEnum.Amendment &&
      this.pendingTransaction.integrationType ===
        PmsIntegrationTypeEnum.LibertyRx
    );
  }

  isSplitNdcsAnyDupes(): boolean {
    const dupTransactions = this.store.selectSnapshot(TransactionsState.duplicateTransactions) || [];
    return dupTransactions.some(x => this.pendingTransaction?.splitNdcs?.some(n => n.drugId === x.drugId));
  }

  //list after a manual removal of some duplicates
  getExistingDrugIds(): number[] {
    return this.pendingTransaction?.splitNdcs?.map(x => x.drugId);
  }

  getDupAmendDialogType(): DupDialogTypeEnum {
    return this.pendingTransaction?.splitNdcs?.length ?
      DupDialogTypeEnum.SplitNdcsAmendment : DupDialogTypeEnum.RegularAmendment;
  }

  getSaveBtnText(): string {
    if (this.dupTransactionsService.isDup(this.pendingTransaction.drugId) || this.isSplitNdcsAnyDupes()) {
      return 'Confirm (dup)';
    }
    return 'Confirm';
  }

  getLocationsToExcludeFromInventorySearch(): ILocationToExclude[] {
    return [
      {id: this.store.selectSnapshot(InventoryState.damagedExpiredLocationID)}, 
      ...this.selectedDrugs?.map(x => ({id: x.locationID, drugID: x.drugID?.toString()}))
    ]
  }

  getLocationToExcludeForTheOriginalNdcDropdown(): string[] {
    return !this.locationsToExclude?.length ? [
      this.store.selectSnapshot(InventoryState.damagedExpiredLocationID),
      ...this.getSelectedDrugsOnLocations()
      .filter(x => x.locationID !== this.splitNdcsForm.controls?.originalTransactionLocationId?.value && x.drugID === this.pendingTransaction.drugId)
      .map(x => x.locationID)
    ] : this.locationsToExclude;
  }

  getLocationToExcludeForTheSplitNdcDropdown(rowN: number): string[] {
    const row = this.splitNdcsForm.controls?.ndcs?.value?.[rowN];
    return [this.store.selectSnapshot(InventoryState.damagedExpiredLocationID),
    ...this.getSelectedDrugsOnLocations()
    .filter(x => x.locationID !== row?.locationId && x.drugID === row?.drugId)
    .map(x => x.locationID)
    ];
  }

  isBackCountEnabledForAny(): boolean {
    return this.backCountService.isAnyRequiresBackCount();
  }

  private attachSplitNdcAmendBackCountControls(splitNdcs: ISplitNdc[]): void {
    splitNdcs?.forEach(ndc => {
      const backCountEnabledForTheActivity = this.backCountService.enabledForActivityAndSchedule(ActivityTypesEnum.Amendment, ndc.schedule, ndc.drugId.toString());

        const newNdc = this.formBuilder.group({
          isBackCountMatches: [null, backCountEnabledForTheActivity ? Validators.required : null],
        });

        (this.splitNdcsForm.get('amendSplitNdcs') as FormArray).push(newNdc);
    });
  }

  private updateOriginalTransactionOnHandWhenLocationChanges(): void {
    this.splitNdcsForm.get('originalTransactionLocationId')?.valueChanges
    .pipe(untilDestroyed(this), distinctUntilChanged())
    .subscribe(id => {

      this.resetBackCount(this.splitNdcsForm.get('originalTransactionIsBackCountMatches'));

      this.store.dispatch(new GetInventoryOnLocation(id, this.pendingTransaction.drugId.toString()))
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        const currentInventoryOnLocation = this.store.selectSnapshot(InventoryState.currentInventoryForNdcOnLocation);
        this.pendingTransaction.starting = currentInventoryOnLocation.onHand;
        this.updateLackOfInventoryModalView();
        this.setC2KeepPartialControl();
        this.cd.markForCheck();
      });
    });
  }

  private setUpDates(): void {
    if (!this.pendingTransaction.statusCodeDate) {
      this.transactionDate = new Date(this.pendingTransaction.createDate);
    } else if (this.pendingTransaction.integrationType === PmsIntegrationTypeEnum.LibertyRx) {
      this.transactionDate = getUTC(this.pendingTransaction.statusCodeDate);
    } else {
      this.transactionDate = new Date(
        this.pendingTransaction.statusCodeDate
      );
    }

    this.fillDate = this.formattedDatePipe.transform(getUTC(this.pendingTransaction.dispenseDate || this.pendingTransaction.createDate), 'custom');

    this.splitNdcsForm.get('transactionDateDisplayValue')?.setValue(
        this.formattedDatePipe.transform(
          this.transactionDate,
          'MMM d a, HH:mm yyyy'
        )
      );
  }

  private setUpBackCount(): void {
    //Back count for regular pending transactions
    if (!this.pendingTransaction.splitNdcs?.length) {
      this.backCountEnabledForTheOriginalPT = this.backCountService.enabledForActivityAndSchedule(this.pendingTransaction.activityType, this.pendingTransaction.schedule, this.pendingTransaction.drugId.toString()) && !this.hideBackCount;
      this.splitNdcsForm.get('originalTransactionIsBackCountMatches').setValidators(this.backCountEnabledForTheOriginalPT ? Validators.required : null);
    } else {
      this.attachSplitNdcAmendBackCountControls(this.pendingTransaction.splitNdcs);
    }
  }

  private resetBackCount(control: AbstractControl): void {
    control.setValue(null);
  }

  private setC2KeepPartialControl(): void {
    if (this.isLackOfInventory) {
      const control = this.splitNdcsForm.get('c2keepPartialAddedQty');
      control.setValidators([Validators.required, 
        Validators.max(this.pendingTransaction.starting), 
        Validators.min(0),
        c2keepPartialAddedQtyValidator.bind(this, () => this.isLackOfInventory)
      ]);
      control.patchValue(this.pendingTransaction.starting);
      control.updateValueAndValidity();
      this.c2keepPartialAddedQtyControlErrorsDescription.max = 'Qty should not exceed ' + this.pendingTransaction.starting;
      this.c2keepPartialAddedQtyControlErrorsDescription.min = 'Qty should be greater than 0';
    }
  }

  private getC2keepPartialAddedQty(): number {
    return this.splitNdcsForm.get('c2keepPartialAddedQty').value || 0;
  }

  private getOriginalNdcCountInSplitNdcWorkflow(splitNdcsCombinedTotal: number = null): number {
    if (!splitNdcsCombinedTotal) {
      const ndcs = this.splitNdcsForm.controls.ndcs as UntypedFormArray;
      splitNdcsCombinedTotal = ndcs.value.reduce(
        (acc: any, ndc: any) =>
          new Decimal(acc).add(new Decimal(Math.abs(+ndc.addedQty))).toNumber(),
        0
      );
    }

    return new Decimal(this.originalNdcTotalCount)
    .sub(new Decimal(splitNdcsCombinedTotal))
    .toNumber();
  }

  private recalcSplitNdcsCombinedTotal(): number {
    const ndcs = this.splitNdcsForm.controls.ndcs as UntypedFormArray;
    return ndcs.value.reduce(
      (acc: any, ndc: any) =>
        new Decimal(acc).add(new Decimal(Math.abs(+ndc.addedQty))).toNumber(),
      0
    );
  }

  private handleRuleViolations(violations: BusinessRuleViolation[]) {
    for (const v of violations) {
      switch (v.id as PendingTransactionRule) {
        case PendingTransactionRule.FutureCycleFill:
          this.showFutureCycleFillAlert = true;
          break;
      }
    }
  } 
}
